import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DollarSign, Plus, Trash2, Save, Tag, Edit2, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingManagement = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState({
    singleBookPrice: 9.99,
    allBooksMonthly: 29.99,
    allBooksYearly: 299.99,
    bulkDiscounts: [
      { quantity: 2, discount: 5 },
      { quantity: 3, discount: 10 },
      { quantity: 4, discount: 15 },
      { quantity: 5, discount: 20 }
    ]
  });
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: '', discountPercentage: 0 });
  const [editingCoupon, setEditingCoupon] = useState(null);

  const loadPricing = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/pricing`);
      setPricing(response.data);
    } catch (error) {
      toast.error('Failed to load pricing configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCoupons = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/coupons`, {
        headers: getAuthHeader()
      });
      setCoupons(response.data);
    } catch (error) {
      toast.error('Failed to load coupons');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadPricing();
    loadCoupons();
  }, [loadPricing, loadCoupons]);

  const sortedBulkDiscounts = useMemo(() =>
    [...pricing.bulkDiscounts].sort((a, b) => a.quantity - b.quantity),
    [pricing.bulkDiscounts]
  );

  const handleCreateCoupon = async () => {
    if (!newCoupon.code || newCoupon.discountPercentage <= 0 || newCoupon.discountPercentage > 100) {
      toast.error('Please enter a valid coupon code and discount percentage (1-100)');
      return;
    }

    try {
      await axios.post(`${API_URL}/admin/coupons`, {
        code: newCoupon.code.toUpperCase(),
        discount_percentage: newCoupon.discountPercentage,
        is_active: true
      }, {
        headers: getAuthHeader()
      });
      toast.success('Coupon created successfully!');
      setNewCoupon({ code: '', discountPercentage: 0 });
      loadCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleToggleCouponStatus = async (code, currentStatus) => {
    try {
      await axios.put(`${API_URL}/admin/coupons/${code}`, {
        is_active: !currentStatus
      }, {
        headers: getAuthHeader()
      });
      toast.success('Coupon status updated!');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to update coupon status');
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Are you sure you want to delete coupon ${code}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/coupons/${code}`, {
        headers: getAuthHeader()
      });
      toast.success('Coupon deleted successfully!');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleUpdateCouponDiscount = async (code, newDiscount) => {
    try {
      await axios.put(`${API_URL}/admin/coupons/${code}`, {
        discount_percentage: parseFloat(newDiscount)
      }, {
        headers: getAuthHeader()
      });
      toast.success('Coupon discount updated!');
      setEditingCoupon(null);
      loadCoupons();
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };


  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/admin/pricing`, pricing, {
        headers: getAuthHeader()
      });
      toast.success('Pricing updated successfully!');
    } catch (error) {
      toast.error('Failed to save pricing configuration');
    }
  };

  const addDiscountTier = () => {
    setPricing({
      ...pricing,
      bulkDiscounts: [
        ...pricing.bulkDiscounts,
        { quantity: pricing.bulkDiscounts.length + 2, discount: 0 }
      ]
    });
  };

  const removeDiscountTier = (index) => {
    setPricing({
      ...pricing,
      bulkDiscounts: pricing.bulkDiscounts.filter((_, i) => i !== index)
    });
  };

  const updateDiscountTier = (index, field, value) => {
    const newDiscounts = [...pricing.bulkDiscounts];
    newDiscounts[index] = {
      ...newDiscounts[index],
      [field]: parseFloat(value) || 0
    };
    setPricing({ ...pricing, bulkDiscounts: newDiscounts });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Pricing Management</h1>
            <p className="text-gray-600 mt-2">Configure subscription and bulk purchase pricing</p>
          </div>
          <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
            <Save className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>

        {/* Subscription Pricing */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-green-600" />
              Subscription Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="singleBookPrice">Single Book (1 month)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="singleBookPrice"
                    type="number"
                    step="0.01"
                    value={pricing.singleBookPrice}
                    onChange={(e) => setPricing({ ...pricing, singleBookPrice: parseFloat(e.target.value) })}
                    className="pl-8"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="allBooksMonthly">All Books (Monthly)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="allBooksMonthly"
                    type="number"
                    step="0.01"
                    value={pricing.allBooksMonthly}
                    onChange={(e) => setPricing({ ...pricing, allBooksMonthly: parseFloat(e.target.value) })}
                    className="pl-8"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="allBooksYearly">All Books (Yearly)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="allBooksYearly"
                    type="number"
                    step="0.01"
                    value={pricing.allBooksYearly}
                    onChange={(e) => setPricing({ ...pricing, allBooksYearly: parseFloat(e.target.value) })}
                    className="pl-8"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Purchase Discounts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                Bulk Purchase Discounts
              </span>
              <Button onClick={addDiscountTier} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-1" />
                Add Tier
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {sortedBulkDiscounts.map((tier, index) => (
                <div key={`tier-${tier.quantity}-${index}`} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <Label className="text-sm">Books</Label>
                    <Input
                      type="number"
                      value={tier.quantity}
                      onChange={(e) => updateDiscountTier(index, 'quantity', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm">Discount %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={tier.discount}
                      onChange={(e) => updateDiscountTier(index, 'discount', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="pt-6">
                    <Button
                      onClick={() => removeDiscountTier(index)}
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-3">Discount Preview</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {sortedBulkDiscounts.map((tier) => (
                  <div key={`preview-${tier.quantity}`} className="text-sm">
                    <span className="font-medium">{tier.quantity} books:</span>
                    <span className="ml-2 text-green-600">{tier.discount}% off</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coupon Codes Management */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Tag className="h-5 w-5 mr-2 text-purple-600" />
              Coupon Codes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Add New Coupon */}
            <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-3">Create New Coupon</h4>
              <div className="flex gap-3">
                <div className="flex-1">
                  <Input
                    placeholder="Coupon Code (e.g., SAVE20)"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    className="uppercase"
                  />
                </div>
                <div className="w-32">
                  <Input
                    type="number"
                    placeholder="Discount %"
                    min="1"
                    max="100"
                    value={newCoupon.discountPercentage || ''}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discountPercentage: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <Button onClick={handleCreateCoupon} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <p className="text-xs text-purple-700 mt-2">
                💡 Tip: Create a 100% discount coupon to provide free access without payment processing
              </p>
            </div>

            {/* Coupons Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Times Used</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {coupons.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                        No coupons created yet. Create your first coupon above!
                      </td>
                    </tr>
                  ) : (
                    coupons.map((coupon) => (
                      <tr key={coupon.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-mono font-semibold text-purple-600">{coupon.code}</span>
                        </td>
                        <td className="px-4 py-3">
                          {editingCoupon === coupon.code ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                defaultValue={coupon.discountPercentage}
                                className="w-20"
                                id={`edit-${coupon.code}`}
                              />
                              <span className="text-sm">%</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => {
                                  const input = document.getElementById(`edit-${coupon.code}`);
                                  handleUpdateCouponDiscount(coupon.code, input.value);
                                }}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => setEditingCoupon(null)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${coupon.discountPercentage === 100 ? 'text-green-600' : 'text-gray-800'}`}>
                                {coupon.discountPercentage}%
                              </span>
                              {coupon.discountPercentage === 100 && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                  FREE ACCESS
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleCouponStatus(coupon.code, coupon.isActive)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              coupon.isActive
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {coupon.isActive ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {coupon.usedBy?.length || 0}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingCoupon(coupon.code)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteCoupon(coupon.code)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Save Button (Bottom) */}
        <div className="mt-6 flex justify-end">
          <Button onClick={handleSave} size="lg" className="bg-green-600 hover:bg-green-700">
            <Save className="h-5 w-5 mr-2" />
            Save Pricing Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingManagement;
